import * as THREE from "three";
import Experience from "./Experience.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import debug from "debug";

const log = debug("app:zoomall");

/**
 * The `Camera` class is responsible for managing the Three.js camera and its controls.
 * It initializes an orthographic camera, sets up event listeners for resizing, and manages user interactions.
 * @category Experience
 */
export default class Camera {
  /**
   * Creates an instance of `Camera`.
   * @param {Object} myObject - The object containing viewer settings.
   */
  constructor(myObject) {
    this.experience = new Experience();
    log(this.experience);
    this.sizes = this.experience.sizes;
    this.eventHub = this.experience.eventHub;
    this.scene = this.experience.scene;
    this.canvas = this.experience.canvas;
    this.myObject = myObject;
    this.setInstance();
    this.setControls();
    this.defineControls();
    this.eventHub.on("resize", (newSize) => {
      this.resize(newSize);
    });
    this.lastCameraYDirection = 1;
    this.isManipulating = false;
    this.manipulationEndTimeout = null;
  }

  cleanup() {
    try {
        // Remove resize event listener
        if (this.eventHub) {
            this.eventHub.off("resize", this.resize);
        }

        if (this.manipulationEndTimeout) {
            clearTimeout(this.manipulationEndTimeout);
            this.manipulationEndTimeout = null;
        }

        // Dispose of OrbitControls
        if (this.controls) {
            this.controls.dispose();
            this.controls = null;
        }

        // Remove the camera from the scene
        if (this.scene && this.instance) {
            this.scene.remove(this.instance);
        }

        // Clear object references
        this.instance = null;
        this.scene = null;
        this.eventHub = null;
        this.sizes = null;
        this.experience = null;
        this.canvas = null;
        this.myObject = null;

        console.log("Camera cleanup completed.");
    } catch (error) {
        console.error("Error during Camera cleanup:", error);
    }
}

  /**
   * Sets up the orthographic camera instance with predefined near, far, and position settings.
   */
  setInstance() {
    this.instance = new THREE.OrthographicCamera(
      this.sizes.reSize.width / -100, // left
      this.sizes.reSize.width / 100, // right
      this.sizes.reSize.height / 100, // top
      this.sizes.reSize.height / -100, // bottom
      0.1, // near
      1000 // far
    );

    // Set the position as you like
    this.instance.position.set(0, 200, 0);
    this.instance.layers.enable(20);
    // Add the camera to the scene
    this.scene.add(this.instance);
  }

  /**
   * Configures orbit controls for the camera.
   */
  setControls() {
    this.controls = new OrbitControls(this.instance, this.canvas);
    this.controls.target.set(0, 0, 0);
    this.controls.mouseButtons = {
      MIDDLE: THREE.MOUSE.ROTATE, // Middle-click for orbiting
      RIGHT: THREE.MOUSE.PAN, // Right-click for panning
    };

    // 🔹 Emit "needsRender" when user interacts with camera; track manipulation for post-processing skip
    this.controls.addEventListener("change", () => {
      this.isManipulating = true;
      if (this.manipulationEndTimeout) clearTimeout(this.manipulationEndTimeout);
      this.manipulationEndTimeout = setTimeout(() => {
        this.isManipulating = false;
        this.manipulationEndTimeout = null;
      }, 150);
      this.eventHub.emit("needsRender", "true");
    });

    this.update();
    this.resources = this.experience.resources;
  }
  /**
   * Defines control parameters such as enabling/disabling rotation, zoom, panning, and setting speed factors.
   */
  defineControls() {
    this.controls.enableRotate = false; //this.myObject.viewer.controls.enableRotate;
    this.controls.enableDamping = false;
    this.controls.rotateSpeed = this.myObject.viewer.controls.rotateSpeed;
    this.controls.enableZoom = this.myObject.viewer.controls.enableZoom;
    this.dampingFactor = this.myObject.viewer.controls.dampFactor;
    this.controls.smoothZoom = this.myObject.viewer.controls.smoothZoom;
    this.controls.enablePan = this.myObject.viewer.controls.enablePan;
    this.controls.maxZoom = 10; // Example max zoom level
    this.controls.minZoom = 0.125;
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    if (isMobile) {
      this.controls.update();
    } else {
      this.controls.update();
    }
  }

  /**
   * Handles window resizing, updating the camera's projection matrix accordingly.
   * @param {Object} newSize - The new dimensions of the viewport.
   */
  resize(newSize) {
    this.instance.left = newSize.width / -100;
    this.instance.right = newSize.width / 100;
    this.instance.top = newSize.height / 100;
    this.instance.bottom = newSize.height / -100;
    this.instance.updateProjectionMatrix();

  }

  /**
   * Updates the camera and checks if the camera's Y-direction has changed,
   * emitting an event when necessary.
   */
  update() {
    const cameraYDirection = Math.sign(this.instance.position.y);

    // 🔹 Reset the near plane to prevent excessive clipping
    this.instance.near = 0.1;
    this.instance.far = 10000;
    this.instance.updateProjectionMatrix();
    
    this.controls.update();

    if (cameraYDirection * this.lastCameraYDirection < 0) {
        this.lastCameraYDirection = cameraYDirection;
        this.eventHub.emit("cameraYDirectionChanged", cameraYDirection);
    }
  }

}
